import React, { useRef, useState } from 'react';
import { Upload, File, X, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { uploadCSV } from '@/services/importService';
import { toastMessage } from '@/utils/toast-message';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  onFileParse: (data: { columns: string[]; rows: any[]; totalRows: number; allData: any[] }) => void;
  isLoading?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect, onFileParse, isLoading = false }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB (backend allows 10MB)

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!file.name.endsWith('.csv')) {
      return 'Please upload a CSV file';
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 10MB';
    }

    return null;
  };

  const parseCSV = async (file: File) => {
    try {
      setUploading(true);
      setError('');

      // Call API to upload and parse CSV
      const response = await uploadCSV(file);

      if (response.success && response.data) {
        setSuccess(true);
        setError('');

        // Pass parsed data to parent
        onFileParse({
          columns: response.data.headers,
          rows: response.data.preview,
          totalRows: response.data.totalRows,
          allData: response.data.data // Full data for import
        });

        toastMessage('CSV file uploaded and parsed successfully', 'success');
      } else {
        setError('Failed to parse CSV file');
        toastMessage('Failed to parse CSV file', 'error');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Error parsing CSV file';
      setError(errorMessage);
      toastMessage(errorMessage, 'error');
      console.error('CSV parsing error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (selectedFile: File) => {
    const validationError = validateFile(selectedFile);

    if (validationError) {
      setError(validationError);
      setFile(null);
      setSuccess(false);
      return;
    }

    setFile(selectedFile);
    setError('');
    setSuccess(false);
    onFileSelect(selectedFile);
    parseCSV(selectedFile);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setError('');
    setSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Upload CSV File
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Upload a CSV file containing your lead data. Maximum 10,000 rows, file size up to 10MB.
        </p>
      </div>

      {/* File Upload Area */}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
          ${
            isDragging
              ? 'border-brandColor bg-cyan-50 dark:bg-cyan-900/20'
              : 'border-gray-300 dark:border-gray-700 hover:border-cyan-400 dark:hover:border-cyan-600'
          }
          ${isLoading || uploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={isLoading || uploading}
        />

        {uploading ? (
          <div className="flex flex-col items-center space-y-4">
            <div className="p-4 bg-cyan-100 dark:bg-cyan-900 rounded-full">
              <Loader2 className="h-12 w-12 text-brandColor dark:text-cyan-400 animate-spin" />
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                Uploading and parsing CSV...
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Please wait while we process your file
              </p>
            </div>
          </div>
        ) : !file ? (
          <div className="flex flex-col items-center space-y-4">
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full">
              <Upload className="h-12 w-12 text-gray-400 dark:text-gray-500" />
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                Drop your CSV file here, or{' '}
                <span className="text-brandColor dark:text-cyan-400 hover:underline">browse</span>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                CSV files only, max 10,000 rows, up to 10MB
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center space-x-3">
              <File className="h-8 w-8 text-brandColor" />
              <div className="text-left">
                <p className="font-medium text-gray-900 dark:text-white">{file.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {(file.size / 1024).toFixed(2)} KB
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveFile();
              }}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-200">Error</p>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && !error && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start space-x-3">
          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-800 dark:text-green-200">File uploaded successfully</p>
            <p className="text-sm text-green-700 dark:text-green-300">
              CSV file parsed and ready for preview
            </p>
          </div>
        </div>
      )}

      {/* CSV Format Guidelines */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">CSV Format Guidelines</h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
          <li>First row should contain column headers</li>
          <li>Each row should represent one lead</li>
          <li>Use commas to separate values</li>
          <li>Enclose values containing commas in double quotes</li>
          <li>Maximum 10,000 rows will be imported</li>
        </ul>
      </div>
    </div>
  );
};

export default FileUpload;
